import { Component, Input, OnChanges, ViewChild } from '@angular/core';
import { faCircleNotch } from '@fortawesome/free-solid-svg-icons';
import { TranslateService } from '@ngx-translate/core';
import { Chart } from 'chart.js/auto';
import zoomPlugin from 'chartjs-plugin-zoom';
import { addDays, addMonths, isAfter, subDays } from 'date-fns';
import { OverlayPanel } from 'primeng/overlaypanel';
import { GlobalVars } from '../../../../../../../shared/global-vars';
import { ModelService } from '../../../../../../../shared/services/model.service';

@Component({
  selector: 'app-modele-modal-graph-realtime',
  templateUrl: './modele-modal-graph-realtime.component.html',
  styleUrl: './modele-modal-graph-realtime.component.scss'
})
export class ModeleModalGraphRealtimeComponent implements OnChanges {
  @Input() modelData: any;
  @Input() identifier: any;

  chart: any;
  selectedPeriodDays: number = 365;
  datasetLabels: { [key: string]: string } = {};
  predictionItems: { label: string, selected: boolean }[] = [];
  tempsReelItems: { label: string, selected: boolean }[] = [];

  selectedCurves = [];
  loading: boolean = true;
  faSpinner = faCircleNotch;
  currentYear!: number;
  selectedDatasets: { [key: string]: boolean } = {};

  colors = GlobalVars.colors;

  pluie: any[] = [];
  piezo: any[] = [];
  etp: any[] = [];
  debit: any[] = [];

  modeZoom: boolean = false;

  @ViewChild('graphActionsTemplate') graphActionsTemplate!: OverlayPanel;

  constructor(private translate: TranslateService,
    private modelService: ModelService) {
    this.currentYear = new Date().getFullYear();
    Chart.register(zoomPlugin);
  }

  legendLabels = {
    Niveau : this.translate.instant('HEADBAND.MODELE.MODAL.GRAPH.LEGENDE_PIEZO_LEVEL'),
    Pluie : this.translate.instant('HEADBAND.MODELE.MODAL.GRAPH.LEGENDE_RAIN_LEVEL'),
    Debit : this.translate.instant('HEADBAND.MODELE.MODAL.GRAPH.LEGENDE_DEBIT_LEVEL'),
    Date: this.translate.instant('HEADBAND.MODELE.MODAL.GRAPH.LEGENDE_DATE')
  };

  ngOnChanges(): void {
    this.datasetLabels = {};
    this.predictionItems = [];
    if (this.modelData) {
      this.loading = false;
      this.initializeLabels();
      this.initializeDatasetSelection();
      this.filterDataByPeriod(this.selectedPeriodDays);
    }
  }

  initializeLabels() {
    let keys = [];
    keys = [
      'PIEZO',
      'PIEZO-REALTIME',
      'RAIN',
      'DEBIT',
      'DEBIT-REALTIME'
    ];

    keys.forEach(key => {
      this.translate.get(`HEADBAND.MODELE.MODAL.GRAPH.${key}`).subscribe((translation: string) => {
        this.datasetLabels[key] = translation;
        this.predictionItems.push({ label: translation, selected: false });
      });
    });
  }

  initializeDatasetSelection() {
    //realtime
    if (this.modelData) {
      const scenario = this.modelData;

      scenario.forEach((element: { SensorType: any; result: any; }) => {
        switch (element.SensorType) {
          case 'Pluie':
            this.pluie = element.result;
            break;
          case 'Piezo':
            this.piezo = element.result;
            break;
          case 'Debit':
            this.debit = element.result;
            break;
          default:
            break;
        }
      });

      this.predictionItems.forEach(item => {
        switch (item.label) {
          case this.datasetLabels['RAIN']:
            item.selected = this.pluie.length > 0;
            break;
          case this.datasetLabels['PIEZO']:
            item.selected = this.piezo.length > 0;
            break;
          case this.datasetLabels['PIEZO-REALTIME']:
            item.selected = this.piezo.length > 0;
            break;
          case this.datasetLabels['DEBIT']:
            item.selected = this.debit.length > 0;
            break;
          case this.datasetLabels['DEBIT-REALTIME']:
            item.selected = this.debit.length > 0;
            break;
          default:
            break;
        }
      });

      this.predictionItems.forEach(item => {
        this.selectedDatasets[item.label] = item.selected;
      });
    }
  }

  toggleDataset(label: string) {
    this.selectedDatasets[label] = !this.selectedDatasets[label];
    this.updateChart();
  }

  filterDataByPeriod(days: number, event?: Event) {
    if (event) {
      event.stopPropagation();
    }
    this.selectedPeriodDays = days;
    this.updateChart();
  }

  updateChart() {
    if (this.modelData && this.modelData.length > 0) {
      let modelEndDate = this.modelService.getLastDate(this.modelData);
      // define the end date and the start date
      if (modelEndDate) {
        let startDate = subDays(modelEndDate, this.selectedPeriodDays);
        if (this.selectedPeriodDays == 365) {
          startDate = new Date(this.currentYear, 0, 1);
        }
        const now = new Date();
        let endDate = addMonths(now, 1);
        if (this.selectedPeriodDays == 365 || this.selectedPeriodDays == 365*10) {
            endDate = addDays(now,1);
        }
        this.drawChart({ startDate, endDate });
      }
    }
  }

  drawChart(range?: { startDate: Date; endDate: Date }) {
    if (this.chart) {
      this.chart.destroy();
    }

    const datasets = this.getDatasets(range);

    let ctx = document.getElementById('chartRealtimeModel') as HTMLCanvasElement;

    if (!ctx) {
      console.error('Failed to acquire chart context');
      return;
    }

    this.chart = new Chart(ctx, {
      type: 'line',
      data: {
        datasets: datasets
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          title: {
            display: true,
            text: "",
            font: {
              size: 18
            }
          },
          legend: {
            display: false
          },
          zoom: {
            zoom: {
              wheel: {
                enabled: true,
              },
              pinch: {
                enabled: true,
              },
              drag: {
                enabled: this.modeZoom
              },
              mode: 'xy',
            },
            pan: {
              enabled: true,
              mode: 'xy'
            },
            limits: {
              x: { min: 'original', max: 'original' },
              y: { min: 'original', max: 'original' }
            }
          }
        },
        interaction: {
          mode: 'nearest',
          axis: 'x',
          intersect: false
        },
        scales: {
          x: {
            type: 'time',
            time: {
              unit: 'day',
              tooltipFormat: 'dd/MM/yyyy HH:mm:ss',
              displayFormats: {
                day: 'dd/MM/yyyy'
              }
            },
            title: {
              display: true,
              text: this.legendLabels.Date
            }
          },
          y: {
            title: {
              display: true,
              text: this.legendLabels.Niveau
            }
          },
          y1: {
            title: {
              display: true,
              text: this.legendLabels.Pluie
            },
            type: 'linear',
            display: true,
            position: 'right',
            grid: {
              drawOnChartArea: false,
            },
          },
          y2: {
            title: {
              display: true,
              text: this.legendLabels.Debit
            },
            type: 'linear',
            display: true,
            position: 'right',
            grid: {
              drawOnChartArea: false,
            },
          },
        }
      }
    });
  }

  getDatasets(range?: { startDate: Date; endDate: Date }) {

    let allDatasets: any;

    allDatasets = [];
    this.modelData.forEach((sensor: { SensorType: any; result: any[]; }) => {
      switch (sensor.SensorType) {
        case "Pluie":
          allDatasets.push(this.createDataset(this.datasetLabels['RAIN'], sensor.result[0], this.colors.pluie, range));
          break;
        case "Piezo":
          allDatasets.push(this.createDataset(this.datasetLabels['PIEZO'], sensor.result[0], this.colors.piezo, range));
          allDatasets.push(this.createDataset(this.datasetLabels['PIEZO-REALTIME'], sensor.result[1], this.colors.piezoRealtime, range));
          break;
        case "Debit":
          allDatasets.push(this.createDataset(this.datasetLabels['DEBIT'], sensor.result[0], this.colors.debit, range));
          allDatasets.push(this.createDataset(this.datasetLabels['DEBIT-REALTIME'], sensor.result[1], this.colors.debitRealtime, range));
          break;
      }
    });

    return allDatasets.filter((dataset: { label: string | number; }) => this.selectedDatasets[dataset.label]);
  }

  createDataset(label: string, data: any[], color: string, range?: { startDate: Date; endDate: Date }) {
    const filteredData = range
      ? data.filter(d => {
        const date = new Date(d.date);
        return date >= range.startDate && date <= range.endDate;
      })
      : data;

    let borderDash: number[] = [];

    let type: string = 'line';
    let yAxisID: string = 'y';

    if (label === this.datasetLabels['RAIN']) {
      type = 'bar';
      yAxisID = 'y1';
    }
    else if (label === this.datasetLabels['DEBIT'] || label === this.datasetLabels['DEBIT-REALTIME']) {
      type = 'line';
      yAxisID = 'y2';
    }

    return {
      type: type,
      label: label,
      data: filteredData.map(d => ({ x: new Date(d.date), y: d.value })),
      borderColor: color,
      borderWidth: 2,
      fill: false,
      hidden: !this.selectedDatasets[label],
      pointRadius: 0.2,
      borderDash: borderDash,
      tension: 0.1,
      yAxisID: yAxisID
    };
  }

  getPredictionClass(label: string): string {
    switch (label) {
      case this.datasetLabels['RAIN']: return 'color-rain';
      case this.datasetLabels['PIEZO']: return 'color-piezo';
      case this.datasetLabels['PIEZO-REALTIME']: return 'color-piezo-realtime';
      case this.datasetLabels['DEBIT']: return 'color-debit';
      case this.datasetLabels['DEBIT-REALTIME']: return 'color-debit-realtime';
      default: return '';
    }
  }

  onGraphActions(event: any): void {
    this.graphActionsTemplate.toggle(event);
  }

  exportChart() {
    const currentDate = new Date().toISOString().slice(0, 10);

    // Générer l'URL de l'image à partir du chart
    const chartDataURL = this.chart.toBase64Image();

    // Créer un lien temporaire pour le téléchargement
    const link = document.createElement('a');
    link.href = chartDataURL;
    link.download = `realtime_${currentDate}.png`;

    // Simuler le clic pour déclencher le téléchargement
    link.click();
  }

  resetZoomBtn() {
    this.chart.resetZoom();
  }

  setModeZoom() {
    this.modeZoom = !this.modeZoom;
    this.chart.options.plugins.zoom.pan.enabled = !this.modeZoom;
    this.chart.options.plugins.zoom.zoom.drag.enabled = this.modeZoom;
    this.chart.update();
  }

}
